import { createAsyncThunk } from '@reduxjs/toolkit'
import get from 'lodash/get'
import toPairs from 'lodash/toPairs'
import { PasswordRulesServerData, UserConfigServiceErrorCode, ConfigItem } from '@lk/api'
import { LanguageService } from 'loankeeper-common/helpers/language'
import { Storage } from 'loankeeper-common/helpers'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { REGULAR_CHANGE_PASSWORD_MODAL } from '../../features/ChangePasswordForm'
import { ThunkApi } from '../index.d'
import { getUserID } from './userConfig.selectors'

export interface LoadUserConfigPayload {
  user: any
  system: any
}

export const loadUserConfigs = createAsyncThunk<LoadUserConfigPayload, void, ThunkApi>(
  '[USER_CONFIG] LOAD_USER_CONFIG',
  async (_, { dispatch, extra, rejectWithValue }) => {
    const { success, data } = await extra.api.userConfig.get()

    if (success) {
      // set language
      const lang = get(data.UserConfig, 'Lang')
      if (lang) {
        LanguageService.setLanguage(lang.toLowerCase())
        Storage.set('currentLang', lang.toLowerCase())
      } else {
        console.error('language not supported')
      }

      if (get(data.UserConfig, 'PasswordExpired')) {
        dispatch(LKModalActions.open({ name: REGULAR_CHANGE_PASSWORD_MODAL, forced: true, message: '' }))
      }

      return {
        user: data.UserConfig,
        system: data.SysConfig,
      }
    }

    return rejectWithValue('Failed to load configs')
  },
)

interface LoadPasswordRulesAttributes {
  userID: number | string
  HashSum_UserID_?: string
}

export const loadPasswordRules = createAsyncThunk<PasswordRulesServerData, LoadPasswordRulesAttributes, ThunkApi>(
  '[USER_CONFIG] LOAD_PASSWORD_RULES',
  async ({ userID, HashSum_UserID_ }, { dispatch, getState, extra, rejectWithValue }) => {
    const state = getState()

    const id = userID || getUserID(state)

    const result = await extra.api.userConfig.getPasswordRules(id, HashSum_UserID_)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError('Failed to load password rules'))

    return rejectWithValue('Failed to load password rules')
  },
)

type ChangePasswordAttributes = {
  password?: string
  newPassword: string
  userID?: number | string
  hash?: string
}

export const changePassword = createAsyncThunk<void, ChangePasswordAttributes, ThunkApi>(
  '[USER_CONFIG] CHANGE_PASSWORD',
  async ({ password, newPassword, userID, hash }, { getState, extra, rejectWithValue }) => {
    const state = getState()

    const id = userID || getUserID(state)

    const result = await extra.api.userConfig.changePassword(id, password, newPassword, hash)

    if (!result.success) {
      let { message } = result.error

      if (result.error.code === UserConfigServiceErrorCode.INVALID_OLD_PASSWORD) {
        message = 'MSG_PASSWD_OLD_BAD'
      }

      return rejectWithValue(message)
    }
  },
)

type ConfigItemAttributes = {
  [index in ConfigItem]: string | number
}

export const setConfigs = createAsyncThunk<void, ConfigItemAttributes, ThunkApi>(
  '[USER_CONFIG] SET_CONFIG_ITEM',
  async (items, { extra, rejectWithValue }) => {
    const pairs = toPairs(items) as Array<[ConfigItem, string]>

    const results = await Promise.all(pairs.map(([key, value]) => extra.api.userConfig.set(key, value)))

    if (results.some((result) => !result.success)) {
      return rejectWithValue('Failed to set config item')
    }
  },
)

export const refreshConfigs = createAsyncThunk<void, void, ThunkApi>(
  '[USER_CONFIG] REFRESH_CONFIGS',
  async (_, { extra, rejectWithValue }) => {
    const result = await extra.api.userConfig.refresh()

    if (!result.success) {
      return rejectWithValue('Failed to refresh')
    }
  },
)
